import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Drawer, Alert, Badge } from 'antd';
import api from '../services/api';
import NotificationCenter from './NotificationCenter';
import TeamSwitcher from './organizer/TeamSwitcher';
import useInvitesCounts from '../hooks/useInvitesCounts';
import { 
  MenuFoldOutlined, 
  MenuUnfoldOutlined,
  UserOutlined,
  TeamOutlined,
  TrophyOutlined,
  LogoutOutlined,
  DashboardOutlined,
  CreditCardOutlined,
  SettingOutlined,
  FileTextOutlined,
  ToolOutlined,
  DollarOutlined,
  BarChartOutlined,
  QuestionCircleOutlined,
  CloudServerOutlined,
  UsergroupAddOutlined,
  GiftOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  MessageOutlined,
  ScheduleOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  SoundOutlined,
  RobotOutlined,
  PieChartOutlined,
  RocketOutlined,
  NotificationOutlined,
  FlagOutlined,
  ExperimentOutlined,
  CrownOutlined,
  PictureOutlined,
  SendOutlined,
  MailOutlined,
  LineChartOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import futezapLogo from '../assets/futezap.jpg';
import PWAInstallButton from './PWAInstallButton';
import PushNotificationButton from './PushNotificationButton';
import AdBanner from './AdBanner';

const { Header, Sider, Content } = Layout;

const AppLayout = ({ children, onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [needsSync, setNeedsSync] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { pending: invitesPending } = useInvitesCounts('organizer');
  const [openMenuKeys, setOpenMenuKeys] = useState([]);

  useEffect(() => {
    if (location.pathname.startsWith('/community/')) {
      setOpenMenuKeys((prev) => prev.includes('comunidade-group') ? prev : [...prev, 'comunidade-group']);
    }
  }, [location.pathname]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      setCurrentUser(user);
      checkSyncStatus(user);
    }
    
    // Listener para logout automático quando token é removido
    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue) {
        onLogout();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [onLogout]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth <= 768) {
        setCollapsed(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const checkSyncStatus = async (user) => {
    if (user?.profile === 'ORGANIZADOR' && user?.customer_id) {
      try {
        const response = await api.get(`/organizer/customer-data/${user.customer_id}`);
        setNeedsSync(!response.data.groupjid || response.data.groupjid.trim() === '');
      } catch (error) {
        console.error('Erro ao verificar status de sincronização:', error);
        setNeedsSync(true); // Mostrar tarja em caso de erro
      }
    }
  };

  const getMenuItems = () => {
    const profile = currentUser?.profile;
    
    if (profile === 'SUPORTE') {
      return [
        {
          key: 'suporte',
          label: 'Suporte',
          type: 'group',
        },
        {
          key: '/dashboard',
          icon: <DashboardOutlined />,
          label: 'Painel',
        },
        {
          key: '/customers',
          icon: <UserOutlined />,
          label: 'Times',
        },
        {
          key: '/players',
          icon: <TeamOutlined />,
          label: 'Jogadores',
        },
        {
          key: '/games',
          icon: <TrophyOutlined />,
          label: 'Jogos',
        },
        {
          key: '/instances',
          icon: <CloudServerOutlined />,
          label: 'Instâncias',
        },
        {
          key: '/subscriptions',
          icon: <CreditCardOutlined />,
          label: 'Assinaturas',
        },
        {
          key: '/monitoring',
          icon: <ToolOutlined />,
          label: 'Monitoramento',
        },
        {
          key: '/monitoring/onboarding-alerts',
          icon: <ScheduleOutlined />,
          label: 'Onboarding',
        },
        {
          key: '/monitoring/chip-failures',
          icon: <ThunderboltOutlined />,
          label: 'Saúde dos Chips',
        },
        {
          key: '/monitoring/expired-teams',
          icon: <DollarOutlined />,
          label: 'Times Expirados',
        },
        {
          key: '/monitoring/irregular-subscriptions',
          icon: <WarningOutlined />,
          label: 'Assinaturas Irregulares',
        },
        {
          key: '/affiliates',
          icon: <UsergroupAddOutlined />,
          label: 'Afiliados',
        },
        {
          key: '/commissions',
          icon: <GiftOutlined />,
          label: 'Comissões',
        },
        {
          key: '/message-logs',
          icon: <MessageOutlined />,
          label: 'Logs de Envio',
        },
        {
          key: '/convocations',
          icon: <ScheduleOutlined />,
          label: 'Convocações',
        },
        {
          key: '/help',
          icon: <QuestionCircleOutlined />,
          label: 'Ajuda',
        },
      ];
    }

    if (profile === 'ADMINISTRADOR') {
      return [
        {
          key: 'suporte',
          label: 'Suporte',
          type: 'group',
        },
        {
          key: '/dashboard',
          icon: <DashboardOutlined />,
          label: 'Painel',
        },
        {
          key: '/metrics',
          icon: <BarChartOutlined />,
          label: 'Métricas',
        },
        {
          key: '/coverage-map',
          icon: <GlobalOutlined />,
          label: 'Mapa Times',
        },
        {
          key: '/players-coverage-map',
          icon: <EnvironmentOutlined />,
          label: 'Mapa Jogadores',
        },
        {
          key: '/customers',
          icon: <UserOutlined />,
          label: 'Times',
        },
        {
          key: '/players',
          icon: <TeamOutlined />,
          label: 'Jogadores',
        },
        {
          key: '/games',
          icon: <TrophyOutlined />,
          label: 'Jogos',
        },
        {
          key: '/instances',
          icon: <CloudServerOutlined />,
          label: 'Instâncias',
        },
        {
          key: '/subscriptions',
          icon: <CreditCardOutlined />,
          label: 'Assinaturas',
        },
        {
          key: 'admin',
          label: 'Admin',
          type: 'group',
        },
        {
          key: '/users',
          icon: <SettingOutlined />,
          label: 'Usuários',
        },
        {
          key: '/audit-logs',
          icon: <FileTextOutlined />,
          label: 'Log de Auditoria',
        },
        {
          key: '/monitoring',
          icon: <ToolOutlined />,
          label: 'Monitoramento',
        },
        {
          key: '/monitoring/onboarding-alerts',
          icon: <ScheduleOutlined />,
          label: 'Onboarding',
        },
        {
          key: '/monitoring/chip-failures',
          icon: <ThunderboltOutlined />,
          label: 'Saúde dos Chips',
        },
        {
          key: '/monitoring/expired-teams',
          icon: <DollarOutlined />,
          label: 'Times Expirados',
        },
        {
          key: '/monitoring/irregular-subscriptions',
          icon: <WarningOutlined />,
          label: 'Assinaturas Irregulares',
        },
        {
          key: '/affiliates',
          icon: <UsergroupAddOutlined />,
          label: 'Afiliados',
        },
        {
          key: '/commissions',
          icon: <GiftOutlined />,
          label: 'Comissões',
        },
        {
          key: '/message-logs',
          icon: <MessageOutlined />,
          label: 'Logs de Envio',
        },
        {
          key: '/convocations',
          icon: <ScheduleOutlined />,
          label: 'Convocações',
        },
        {
          key: '/audios',
          icon: <SoundOutlined />,
          label: 'Áudios',
        },
        {
          key: '/monitoring/ai-support',
          icon: <RobotOutlined />,
          label: 'AI Suporte',
        },
        {
          key: '/monitoring/at-risk',
          icon: <WarningOutlined />,
          label: 'Times em risco',
        },
        {
          key: '/monitoring/payments',
          icon: <DollarOutlined />,
          label: 'Pagamentos',
        },
        {
          key: '/monitoring/churn',
          icon: <PieChartOutlined />,
          label: 'Churn Analytics',
        },
        {
          key: '/monitoring/insights',
          icon: <BarChartOutlined />,
          label: 'Insights',
        },
        {
          key: '/monitoring/reengagement',
          icon: <RocketOutlined />,
          label: 'Reengajamento',
        },
        {
          key: '/monitoring/organizer-broadcast',
          icon: <SendOutlined />,
          label: 'Broadcast organizadores',
        },
        {
          key: '/monitoring/stats-adoption',
          icon: <TrophyOutlined />,
          label: 'Adoção Estatísticas',
        },
        {
          key: '/monitoring/overview',
          icon: <DashboardOutlined />,
          label: 'Overview',
        },
        {
          key: '/monitoring/moderacao',
          icon: <FlagOutlined />,
          label: 'Moderação',
        },
        {
          key: '/monitoring/receita-analytics',
          icon: <LineChartOutlined />,
          label: 'Receita & Grupos',
        },
        {
          key: '/admin/features',
          icon: <ExperimentOutlined />,
          label: 'Features',
        },
        {
          key: '/admin/banners',
          icon: <PictureOutlined />,
          label: 'Banners',
        },
        {
          key: '/admin/landing-pages',
          icon: <GlobalOutlined />,
          label: 'Landing Pages',
        },
        {
          key: '/admin/cobranca-comunidade',
          icon: <DollarOutlined />,
          label: 'Cobrança Comunidade',
        },
        {
          key: '/admin/comunidade/financeiro-health',
          icon: <WarningOutlined />,
          label: 'Health Financeiro',
        },
        {
          key: 'pelada',
          label: 'Pelada',
          type: 'group',
        },
        {
          key: '/team-config',
          icon: <ToolOutlined />,
          label: 'Configuração',
        },
        {
          key: '/organizer-players',
          icon: <TeamOutlined />,
          label: 'Jogadores',
        },
        {
          key: '/organizer-games',
          icon: <TrophyOutlined />,
          label: 'Jogos',
        },
        {
          key: '/escalacao',
          icon: <AppstoreOutlined />,
          label: 'Escalação',
        },
        {
          key: '/rankings',
          icon: <CrownOutlined />,
          label: 'Ranking',
        },
        {
          key: '/my-subscription',
          icon: <CreditCardOutlined />,
          label: 'Minha Assinatura',
        },
        {
          key: '/help',
          icon: <QuestionCircleOutlined />,
          label: 'Ajuda',
        },
      ];
    }
    
    if (profile === 'ORGANIZADOR') {
      return [
        {
          key: 'pelada',
          label: 'Pelada',
          type: 'group',
        },
        {
          key: '/team-config',
          icon: <ToolOutlined />,
          label: 'Configuração',
        },
        {
          key: '/organizer-players',
          icon: <TeamOutlined />,
          label: 'Jogadores',
        },
        {
          key: '/organizer-games',
          icon: <TrophyOutlined />,
          label: 'Jogos',
        },
        {
          key: '/escalacao',
          icon: <AppstoreOutlined />,
          label: 'Escalação',
        },
        {
          key: '/rankings',
          icon: <CrownOutlined />,
          label: 'Ranking',
        },
        {
          key: '/frequency',
          icon: <BarChartOutlined />,
          label: 'Frequência',
        },
        {
          key: '/cobrancas',
          icon: <DollarOutlined />,
          label: 'Cobranças',
        },
        {
          key: '/team-organizers',
          icon: <UserOutlined />,
          label: 'Administradores',
        },
        {
          key: '/my-subscription',
          icon: <CreditCardOutlined />,
          label: 'Minha Assinatura',
        },
        {
          key: '/my-account',
          icon: <UserOutlined />,
          label: 'Minha Conta',
        },
        {
          key: '/referrals',
          icon: <GiftOutlined />,
          label: 'Indicações',
        },
        {
          key: 'comunidade-group',
          icon: <UsergroupAddOutlined />,
          label: 'Comunidade',
          children: [
            { key: '/community/team/profile', icon: <UserOutlined />, label: 'Perfil do time' },
            { key: '/community/elenco', icon: <UsergroupAddOutlined />, label: 'Elenco' },
            { key: '/community/mural', icon: <NotificationOutlined />, label: 'Notícias' },
            { key: '/community/jogos', icon: <TrophyOutlined />, label: 'Tabela de jogos' },
            { key: '/community/amistosos', icon: <RocketOutlined />, label: 'Amistosos' },
            { key: '/community/map', icon: <EnvironmentOutlined />, label: 'Mapa' },
            {
              key: '/community/invites',
              icon: <MailOutlined />,
              label: (
                <span>
                  Convites {invitesPending > 0 && <Badge count={invitesPending} size="small" style={{ marginLeft: 6 }} />}
                </span>
              ),
            },
          ],
        },
        {
          key: '/help',
          icon: <QuestionCircleOutlined />,
          label: 'Ajuda',
        },
      ];
    }

    return [];
  };
  
  const menuItems = getMenuItems();
  const isOrganizer = currentUser?.profile === 'ORGANIZADOR';
  // Estilo premium (gradient sidebar, header claro sticky, cards, etc.) aplicado
  // a TODOS os perfis. `isOrganizer` continua sendo usado só pra comportamentos
  // específicos: rota home, push button e notification center.
  const isPremium = true;

  const currentPageLabel = (() => {
    const flat = menuItems.flatMap((item) => (item.children ? item.children : [item]))
      .filter((item) => item.key && item.key.startsWith('/'));
    const match = flat.find((item) => location.pathname.startsWith(item.key));
    return match?.label || 'Painel';
  })();

  const handleMenuClick = ({ key }) => {
    navigate(key);
    if (isMobile) {
      setDrawerVisible(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('community_token');
    localStorage.removeItem('community_user');
    onLogout();
  };

  const toggleMenu = () => {
    if (isMobile) {
      setDrawerVisible(!drawerVisible);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const menuContent = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        style={{
          height: isPremium ? 80 : 64,
          margin: isPremium ? '8px 16px 24px' : 16,
          padding: isPremium ? '8px 0' : 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer'
        }}
        onClick={() => navigate(isOrganizer ? '/team-config' : '/dashboard')}
      >
        {collapsed && !isMobile ? (
          <img src={futezapLogo} alt="Futezap" style={{ height: 32, width: 32, borderRadius: isPremium ? 10 : 4 }} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={futezapLogo} alt="Futezap" style={{ height: 40, width: 40, borderRadius: isPremium ? 10 : 4 }} />
            <span style={{ color: 'white', fontWeight: 'bold', fontSize: isPremium ? 17 : 16 }}>Futezap Admin</span>
          </div>
        )}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        openKeys={openMenuKeys}
        onOpenChange={setOpenMenuKeys}
        items={menuItems}
        onClick={handleMenuClick}
        style={isPremium ? { background: 'transparent', border: 'none', flex: 1 } : { flex: 1 }}
      />
      {isPremium && (!collapsed || isMobile) && (
        <div
          style={{
            padding: '16px',
            borderTop: '1px solid rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <UserOutlined style={{ color: 'rgba(255,255,255,0.85)' }} />
            <span
              style={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: 13,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {currentUser?.username || 'Organizador'}
            </span>
          </div>
          <Button
            type="text"
            size="small"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{ color: 'rgba(255,255,255,0.85)' }}
          />
        </div>
      )}
    </div>
  );

  return (
    <Layout style={{ 
      minHeight: '100vh', 
      ...(isMobile && { height: '100vh', overflow: 'hidden' })
    }}>
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={280}
          className={isPremium ? 'organizer-sider' : undefined}
          style={isPremium ? { background: 'linear-gradient(180deg, #0f172a 0%, #164e63 50%, #0e7c4a 100%)' } : undefined}
        >
          {menuContent}
        </Sider>
      )}

      {isMobile && (
        <Drawer
          title={null}
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          className={isPremium ? 'organizer-sider' : undefined}
          bodyStyle={{
            padding: 0,
            background: isPremium
              ? 'linear-gradient(180deg, #0f172a 0%, #164e63 50%, #0e7c4a 100%)'
              : '#001529',
            height: '100%',
          }}
          width={isOrganizer ? 280 : 250}
        >
          {menuContent}
        </Drawer>
      )}
      <Layout>
        <Header style={isPremium ? {
            padding: 0,
            background: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            borderBottom: '1px solid rgba(15,23,42,0.06)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: isMobile ? 'fixed' : 'sticky',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            width: '100%',
            height: 64,
          } : {
            padding: 0,
            background: '#00B96B',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: isMobile ? 'fixed' : 'static',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            width: '100%'
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 16, minWidth: 0, flex: 1 }}>
            <Button
              type="text"
              icon={isMobile ? <MenuFoldOutlined style={{fontSize: 20}} /> : (collapsed ? <MenuUnfoldOutlined style={{fontSize: 20}}/> : <MenuFoldOutlined style={{fontSize: 20}}/>)}
              onClick={toggleMenu}
              style={{ fontSize: '16px', width: isMobile ? 48 : 64, height: 64, color: isPremium ? '#0f172a' : 'white', flexShrink: 0 }}
            />
            {isPremium && (
              <span style={{
                color: '#0f172a',
                fontWeight: 700,
                fontSize: isMobile ? 14 : 16,
                fontFamily: 'Manrope, sans-serif',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}>
                {currentPageLabel}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 16, marginRight: isMobile ? 8 : 16, flexShrink: 0 }}>
            {!isMobile && (
              <span style={{ color: isPremium ? '#334155' : 'white', fontWeight: 'bold', lineHeight: '20px', fontSize: '16px' }}>
                Olá, {currentUser?.username || 'Usuário'}
              </span>
            )}
            {isOrganizer && <TeamSwitcher />}
            {currentUser?.profile === 'ORGANIZADOR' && <NotificationCenter userType="organizer" />}
            {isOrganizer && <PushNotificationButton compact={isMobile} />}

            <Button
              type="text"
              icon={<LogoutOutlined style={{fontSize: '16px'}}/>}
              onClick={handleLogout}
              style={{ color: isPremium ? '#0f172a' : 'white', padding: isMobile ? '0 8px' : undefined }}
            >
              {!isMobile && <span style={{fontSize: 16}}>Sair</span>}
            </Button>
          </div>
        </Header>
        <Content style={{
          margin: isMobile ? '16px 8px' : '24px 16px',
          padding: isMobile ? 16 : 24,
          background: isPremium ? '#f8fafc' : '#fff',
          borderRadius: isPremium ? 16 : 0,
          marginTop: isMobile ? '64px' : '24px',
          ...(isMobile && {
            height: 'calc(100vh - 80px)',
            overflow: 'auto'
          })
        }}>
          {needsSync && currentUser?.profile === 'ORGANIZADOR' && (
            <Alert
              message="Você precisa sincronizar o futezap com seu grupo"
              type="warning"
              action={
                <Button 
                  size="small" 
                  type="primary" 
                  onClick={() => navigate('/sincronizar')}
                >
                  Clique aqui
                </Button>
              }
              closable={false}
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}
          {isOrganizer && <AdBanner />}
          {children}
        </Content>
      </Layout>
      {isOrganizer && <PWAInstallButton />}
    </Layout>
  );
};

export default AppLayout;